import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Controller, useController } from "react-hook-form";
import { TextField } from "@mui/material";
import { AlertCircle, CheckCircle, Key, Lock } from "react-feather";
import styles from "./SharedPasswordFields.module.css";

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// How many accounts already use an email (any company, any role)
export const adminAccountsLookup = (apiToken) => (email) =>
  axios.get(process.env.REACT_APP_API_URL + "v1/users/email-accounts", {
    params: { email },
    headers: { Authorization: `Bearer ${apiToken}` },
  }).then((response) => response.data?.data?.accounts || 0);

export const publicAccountsLookup = (utoken) => (email) =>
  axios.post(process.env.REACT_APP_API_URL + "v1/check-email", { email, utoken: utoken || "" })
    .then((response) => response.data?.data?.accounts || 0);

// Used when the form has no error class of its own
const errorStyle = { display: "block", marginTop: 4, fontSize: 12, color: "#d32f2f" };

/**
 * Password part of add and sign-up forms. One password is shared by every account with
 * the same email, so when the email already has accounts the person chooses to keep that
 * password or set a new one, which then replaces it on all of them.
 * Adds password_mode ("keep" | "new"), password and conf_password to the form data.
 *
 * variant "admin": keeping needs no password field.
 * variant "public": keeping asks for the existing password; a new one is confirmed by a
 * code the backend emails (the form handles the VERIFY response).
 */
const SharedPasswordFields = ({ control, watch, errors, lookupAccounts, variant = "admin", minLength = 6, groupClassName, fullGroupClassName, errorClassName, inputClasses }) => {
  const email = watch("email");
  const [accounts, setAccounts] = useState(0);
  const { field: modeField } = useController({ name: "password_mode", control, defaultValue: "new" });
  const setMode = modeField.onChange;

  const lookupRef = useRef(lookupAccounts);
  lookupRef.current = lookupAccounts;

  useEffect(() => {
    const value = (email || "").trim();
    if (!EMAIL_REGEX.test(value)) {
      setAccounts(0);
      return undefined;
    }

    let active = true;
    const timer = setTimeout(() => {
      Promise.resolve(lookupRef.current(value))
        .then((count) => active && setAccounts(Number(count) || 0))
        .catch(() => active && setAccounts(0));
    }, 500);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [email]);

  // An email already in use starts on "keep same password"
  useEffect(() => {
    setMode(accounts > 0 ? "keep" : "new");
  }, [accounts, setMode]);

  const keep = accounts > 0 && modeField.value === "keep";
  const accountsText = `${accounts} ${accounts === 1 ? "account" : "accounts"}`;

  const options = [
    {
      value: "keep",
      icon: <Lock />,
      title: "Keep same password",
      sub: variant === "admin" ? "Logs in with their current password" : "Log in with your current password",
    },
    {
      value: "new",
      icon: <Key />,
      title: "Set new password",
      sub: `Replaces it on ${accounts === 1 ? "that account" : `all ${accountsText}`}` + (variant === "public" ? " (confirmed by email code)" : ""),
    },
  ];

  return (<>
    {accounts > 0 && (
      <div className={fullGroupClassName || groupClassName}>
        <div className={styles.Banner}>
          <div className={styles.BannerHead}>
            <AlertCircle className={styles.BannerIcon} />
            <div>
              <p className={styles.BannerTitle}>This email already has {accountsText}</p>
              <p className={styles.BannerSub}>One password works for all of them. Choose one:</p>
            </div>
          </div>
          <div className={styles.Options} role="radiogroup" aria-label="Password choice">
            {options.map((option) => {
              const active = modeField.value === option.value;
              return (<button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={active}
                className={`${styles.Option} ${active ? styles.OptionActive : ""}`}
                onClick={() => setMode(option.value)}
              >
                <span className={styles.OptionIcon}>{option.icon}</span>
                <span>
                  <span className={styles.OptionTitle}>{option.title}</span>
                  <span className={styles.OptionSub}>{option.sub}</span>
                </span>
                {active && <CheckCircle className={styles.OptionCheck} />}
              </button>);
            })}
          </div>
        </div>
      </div>
    )}

    {!(keep && variant === "admin") && (
      <div className={groupClassName}>
        <Controller
          name="password"
          control={control}
          shouldUnregister
          rules={{
            required: "This field is Required",
            ...(keep ? {} : { minLength: { value: minLength, message: `Use at least ${minLength} characters` } }),
          }}
          render={({ field }) => (<TextField
            {...field}
            value={field.value || ""}
            classes={inputClasses}
            label={keep ? "Your existing password*" : "Password*"}
            variant="outlined"
            type="password"
            autoComplete={keep ? "current-password" : "new-password"}
            fullWidth
          />)}
        />
        {errors?.password && (<span className={errorClassName} style={errorClassName ? undefined : errorStyle}>{errors.password.message}</span>)}
      </div>
    )}

    {!keep && (
      <div className={groupClassName}>
        <Controller
          name="conf_password"
          control={control}
          shouldUnregister
          rules={{
            required: "This field is Required",
            validate: (value) => value === watch("password") || "Your passwords do not match",
          }}
          render={({ field }) => (<TextField
            {...field}
            value={field.value || ""}
            classes={inputClasses}
            label="Confirm Password*"
            variant="outlined"
            type="password"
            autoComplete="new-password"
            fullWidth
          />)}
        />
        {errors?.conf_password && (<span className={errorClassName} style={errorClassName ? undefined : errorStyle}>{errors.conf_password.message}</span>)}
      </div>
    )}
  </>);
};

export default SharedPasswordFields;
